import React, { useContext, useEffect, useState } from 'react';
import { Button, ButtonGroup, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from 'reactstrap';
import { toast } from "react-toastify";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { ItemSite, ItemTypeahead, ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, UserContext, storage } from '../utils';

export default function ItemLookup() {
   const { currentUser } = useContext(UserContext);
   let modalClassName = currentUser.theme ? `${currentUser.theme}` : "lightTheme";
   let savedDevice = JSON.parse(storage.getItem("deviceType"));
   const isMobile = savedDevice.isMobile;
   const hasScanner = savedDevice.hasScanner;

   const [data, setData] = useState(null);
   const [id, setBarcode] = useState(null);
   const [item, setItem] = useState(null);
   const [loaded, setLoaded] = useState(true);
   const [open, setOpen] = useState(null);
   const [search, setSearch] = useState(null);
   const [manualEntry, setManual] = useState(null);
   const sleep = ms => new Promise(r => setTimeout(r, ms));

   useEffect(() => {
      inputFocus("searchInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }

      if (!id) {
         inputFocus("searchInput");
         return;
      }

      setLoaded(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [manualEntry])

   useEffect(() => {
      if (search) {
         inputFocus("itemInput");
         return;
      }
      inputFocus("searchInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [search])

   useEffect(() => {
      // if device has built-in scanner see if a scan was completed
      if (id && hasScanner && !manualEntry) {
         setLoaded(false);
      }
      if (manualEntry)
         inputFocus("manualInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id])

   useEffect(() => {
      if (!loaded && !id) {
         if (!item) // Not searching by item
            toast.warning("You must provide a barcode");
         setLoaded(true);
         inputFocus("searchInput");
         return;
      }
      if (!loaded && id) {
         // If a QR code was scanned then parse out just the barcode from the data
         var code = constants.GetRealBarcode(id);
         api
            .post(`Mobile/LookupItemByBarcode/${code}`)
            .then(response => {
               if (response.data.success) {
                  setData(response.data.item);
               }
               else {
                  setData(null);
                  toast.error(response.data.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               setData(null);
            })
            .finally(() => {
               setLoaded(true);
               inputFocus("searchInput");
            })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loaded])

   function handleItemChange() {
      if (item === null) {
         setData(null);
         inputFocus("searchInput");
         return;
      }

      // escape the forward slash character or pound symbol if in the item so it won't mess up the URL
      // Example = MP.11/64S40007 this causes the url to look like it has an extra parameter because
      // of the / character so the URL is broken and a Not Found error is thrown.
      var encodedItem = item.itemNumber.replace(/\//g, '%2F').replace(/#/g, '%23');

      setLoaded(false);
      api
         .post(`Mobile/LookupByItem/${encodedItem.toUpperCase()}`)
         .then(response => {
            if (response.data.success) {
               setData(response.data.item);
            }
            else {
               setData({});
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            setData({});
            api.catchHandler(error);
         })
         .finally(() => {
            setLoaded(true);
            setItem(null);
            inputFocus("searchInput");
         });
   }

   const handleItemKeypress = e => {
      inputFocus("itemInput");
   };

   const handleKeypress = e => {
      if (e.charCode === 13 || e.key === 'Enter') {
         setManual(null);
         return;
      }
      inputFocus("manualInput");
   };

   async function inputFocus(fieldname) {
      await sleep(80);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   async function lookup(barcode) {
      setBarcode(barcode.toUpperCase());
      if (manualEntry)
         inputFocus("manualInput");
      else
         inputFocus("searchInput");
   }

   function onSiteToggle() {
      inputFocus("searchInput");
   }

   return (
      <>
         <DesktopContainer screenName="Item Lookup">
            <ServiceThreadSpinner on={!loaded}>
               {/* Scan Button */}
               {!hasScanner && isMobile ?
                  <div>
                     <Row className="justify-content-center">
                        <Col xs="12">
                           <Button title="scan entry" className="shredButton w-100 btn-md" onClick={() => setOpen(1)}>
                              Scan Barcode
                           </Button>
                        </Col>
                     </Row>
                  </div>
                  : null
               }
               <div>
                  {/* Item Search Button */}
                  <Row className="justify-content-center">
                     <Col xs="12">
                        <Button title="item search" className="shredButton w-100 btn-md my-1" onClick={() => setSearch(1)}>
                           Item Search
                        </Button>
                     </Col>
                  </Row>
                  {/* Manual Entry Button */}
                  <Row className="justify-content-center">
                     <Col xs="12">
                        <Button title="manual entry" className="shredButton w-100 btn-md" onClick={() => setManual(1)}>
                           Manual Entry
                        </Button>
                     </Col>
                  </Row>
                  {/* Built-in Scanner Barcode */}
                  {isMobile ?
                     <div>
                        <Row className="lineheight8">&nbsp;</Row>
                        <Row className="justify-content-center">
                           <Col xs="12">
                              <Input
                                 name="searchInput"
                                 placeholder="Focus here to scan barcode"
                                 className="h-100"
                                 onChange={(e) => lookup(e.target.value)}
                              />
                           </Col>
                        </Row>
                     </div>
                     : null
                  }
               </div>
               <br />
               {loaded && data ?
                  <Table striped className="data-table mobileGrayBgAlt">
                     <tbody>
                        <tr>
                           <td className="data-cell py-0" width="125px">
                              <label className="text-black h6 mx-2 my-0 fw-bold">Item:</label>
                           </td>
                           <td className="data-cell py-0">
                              <label className="text-black h6 my-1 noWrap">{data.itemWithDesc}</label>
                           </td>
                        </tr>
                        <tr>
                           <td className="data-cell my-0 py-0">
                              <label className="text-black h6 mx-2 my-0 fw-bold">Class:</label>
                           </td>
                           <td className="data-cell my-0 py-0">
                              <label className="text-black h6 my-1">{data.itemClass}</label>
                           </td>
                        </tr>
                        {data.siteId ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Site:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.siteId}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.caseStatus ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Status:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.caseStatus}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.lotNumber ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Case #:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1 noWrap">{data.lotNumber}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.uofm ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">UofM:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.uofm}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.lastCountQty > 0 ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Last Count:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.lastCountQty.toLocaleString()}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.caseQty >= 0 && data.uofm.toLowerCase().includes('qty') ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Qty:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.caseQty.toLocaleString()}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.grossWeight > 0 ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Gross:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.grossWeight.toLocaleString()} LBS</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.netWeight > 0 ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Net:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.netWeight.toLocaleString()} LBS</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.tareWeight > 0 ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Tare:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.tareWeight.toLocaleString()} LBS</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.palletWeight > 0 ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Pallet:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.palletWeight.toLocaleString()}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.productionDate ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Added:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.productionDate}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.producedBy ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Added By:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1 noWrap">{data.producedBy}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.updated && data.updated.length > 0 ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Updated:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.updated}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.updatedBy && data.updatedBy.length > 0 ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Update By:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.updatedBy}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.fulfilledDate ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Fulfilled:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.fulfilledDate}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.disassembledDate ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Disassembled:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.disassembledDate}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.receivedBy ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Rcvd By:</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.receivedBy}</label>
                              </td>
                           </tr>
                           : null
                        }
                        {data.vendors ?
                           <tr>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 mx-2 my-0 fw-bold">Vendor(s):</label>
                              </td>
                              <td className="data-cell my-0 py-0">
                                 <label className="text-black h6 my-1">{data.vendors}</label>
                              </td>
                           </tr>
                           : null
                        }
                     </tbody>
                  </Table>
                  : null
               }

               {/* Site Information */}
               {loaded && data ?
                  <div className="filter-header">
                     {data.sites && data.sites.length > 0
                        ? <ItemSite data={data.sites} onToggle={onSiteToggle} />
                        : <Row className="justify-content-center">
                           <Col className="mobileGrayBg my-0 py-2 px-2 h5" xs="10">
                              <span style={{ color: "black" }}>
                                 No On Hand Quantities
                              </span>
                           </Col>
                        </Row>
                     }
                  </div>
                  : null
               }

               {/* Scan a Barcode */}
               <Modal isOpen={open !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Scan a Barcode" />
                  <ModalBody
                     children={
                        <BarcodeScannerComponent
                           height={350}
                           width={350}
                           onUpdate={(err, result) => {
                              if (result) {
                                 setBarcode(result.text.toUpperCase());
                                 setOpen(null);
                                 setLoaded(false);
                                 inputFocus("searchInput");
                              }
                              else {
                                 setBarcode(null);
                              }
                           }}
                           facingMode={"environment"}
                        />
                     }
                  />
                  <ModalFooter>
                     <Button
                        className="shredButton ms-7"
                        onClick={() => {
                           setOpen(null);
                           inputFocus("searchInput");
                        }
                        }>Cancel
                     </Button>
                  </ModalFooter>
               </Modal>


               {/* Item Search Entry */}
               <Modal isOpen={search !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Item Search" />
                  <ModalBody
                     children={
                        <ItemTypeahead
                           name="itemInput"
                           value={item}
                           onChange={setItem}
                           onKeyPress={handleItemKeypress}
                        />
                     }
                  />
                  <ModalFooter>
                     <ButtonGroup>
                        <Button
                           children="Continue"
                           className="button-success"
                           onClick={() => {
                              setSearch(null);
                              handleItemChange();
                           }}
                        />
                        <Button
                           children="Cancel"
                           className="button-danger"
                           onClick={() => {
                              setItem(null);
                              setSearch(null);
                              inputFocus("searchInput");
                           }}
                        />
                     </ButtonGroup>
                  </ModalFooter>
               </Modal>


               {/* Manual Barcode Entry */}
               <Modal isOpen={manualEntry !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Enter Barcode" />
                  <ModalBody
                     children={
                        <Input
                           name="manualInput"
                           placeholder="Enter barcode"
                           className="h-100"
                           onChange={(e) => lookup(e.target.value.toUpperCase())}
                           onKeyPress={handleKeypress}
                        />
                     }
                  />
                  <ModalFooter>
                     <ButtonGroup>
                        <Button children="Retrieve" className="button-success" onClick={() => { setManual(null); }} />
                        <Button
                           children="Cancel"
                           className="button-danger"
                           onClick={() => {
                              setBarcode(null);
                              setManual(null);
                              inputFocus("searchInput");
                           }}
                        />
                     </ButtonGroup>
                  </ModalFooter>
               </Modal>

            </ServiceThreadSpinner>
         </DesktopContainer >
      </>
   );
}
